import { STORAGE_KEYS, SORT_FIELDS, DEFAULT_SORT_FIELD, ensureSettings, state } from './state';
import { sanitizePercentage, sanitizeCurrency } from './formatting';
import { mapRemoteDocument } from './lotOperations';
import { ensureLocalStructures } from './dataMapping';
import type { Lot, PortalSettings } from '../types';

export function normalizeSortField(value: string | undefined): string {
  if (!value) return DEFAULT_SORT_FIELD;
  return value === SORT_FIELDS.CLOSE || value === SORT_FIELDS.UPDATED ? value : DEFAULT_SORT_FIELD;
}

export function normalizeSortDirection(value: string | undefined): string {
  if (!value) return 'desc';
  return value === 'asc' ? 'asc' : 'desc';
}

export function loadSettingsFromStorage() {
  const settings: PortalSettings = ensureSettings();
  settings.timeZone =
    typeof settings.timeZone === 'string' && settings.timeZone ? settings.timeZone : undefined;
  settings.zipCode = typeof settings.zipCode === 'string' ? settings.zipCode : '';
  settings.distanceUnits =
    settings.distanceUnits === 'mi' || settings.distanceUnits === 'km'
      ? settings.distanceUnits
      : 'auto';
  settings.sortField = normalizeSortField(settings.sortField);
  settings.sortDirection = normalizeSortDirection(settings.sortDirection);
  settings.taxRate = sanitizePercentage(settings.taxRate, 0);
  settings.bidderPremium = sanitizePercentage(settings.bidderPremium, 15);
  settings.sellerCosts = sanitizePercentage(settings.sellerCosts, 14);
  settings.fixedLotCost = sanitizeCurrency(settings.fixedLotCost, 0);
  settings.targetMarginPct = sanitizePercentage(settings.targetMarginPct, 30);
  settings.groupByAuction = settings.groupByAuction === true;
  settings.budgetCap =
    typeof settings.budgetCap === 'number' &&
    Number.isFinite(settings.budgetCap) &&
    settings.budgetCap > 0
      ? settings.budgetCap
      : null;
  try {
    const rawSettings = localStorage.getItem(STORAGE_KEYS.SETTINGS);
    if (rawSettings) {
      const parsed = JSON.parse(rawSettings);
      if (parsed && typeof parsed === 'object') {
        settings.timeZone =
          typeof parsed.timeZone === 'string' && parsed.timeZone ? parsed.timeZone : undefined;
        const parsedZip = typeof parsed.zipCode === 'string' ? parsed.zipCode.trim() : '';
        settings.zipCode = parsedZip || '';
        const du = typeof parsed.distanceUnits === 'string' ? parsed.distanceUnits : 'auto';
        settings.distanceUnits = du === 'mi' || du === 'km' ? du : 'auto';
        settings.sortField = normalizeSortField(parsed.sortField);
        settings.sortDirection = normalizeSortDirection(parsed.sortDirection);
        settings.taxRate = sanitizePercentage(parsed.taxRate, 0);
        settings.bidderPremium = sanitizePercentage(parsed.bidderPremium, 15);
        settings.sellerCosts = sanitizePercentage(parsed.sellerCosts, 14);
        settings.fixedLotCost = sanitizeCurrency(parsed.fixedLotCost, 0);
        settings.targetMarginPct = sanitizePercentage(parsed.targetMarginPct, 30);
        settings.groupByAuction = parsed.groupByAuction === true;
        settings.budgetCap =
          typeof parsed.budgetCap === 'number' &&
          Number.isFinite(parsed.budgetCap) &&
          parsed.budgetCap > 0
            ? parsed.budgetCap
            : null;
      }
    }
    if (!settings.zipCode) {
      const legacyZip = localStorage.getItem('userZipCode');
      if (typeof legacyZip === 'string' && legacyZip.trim()) {
        settings.zipCode = legacyZip.trim();
      }
    }
  } catch {
    /* ignore */
  }
  state.sort.field = normalizeSortField(settings.sortField);
  state.sort.direction = normalizeSortDirection(settings.sortDirection);
}

export function loadLocalLots() {
  loadSettingsFromStorage();
  if (state.mode === 'auth0') {
    state.lots = [];
    return;
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.LOTS);
    if (!raw) {
      state.lots = [];
      return;
    }
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      state.lots = parsed
        .map(function (item: unknown) {
          if (!item || typeof item !== 'object') return null;
          const record = item as Record<string, unknown>;
          if (record.document && typeof record.document === 'object') {
            const mappedDoc = mapRemoteDocument(record.document as Record<string, unknown>);
            if (mappedDoc) {
              mappedDoc.remote = false;
              return ensureLocalStructures(mappedDoc);
            }
          }
          if (record.lot || record.overlay) {
            const mapped = mapRemoteDocument(record);
            if (mapped) {
              mapped.remote = false;
              return ensureLocalStructures(mapped);
            }
          }
          const copy = Object.assign({}, record);
          if (copy.updatedAt != null) {
            const updatedTs =
              typeof copy.updatedAt === 'number'
                ? copy.updatedAt
                : Date.parse(copy.updatedAt as string);
            copy.updatedAt = Number.isFinite(updatedTs) ? updatedTs : undefined;
          }
          if (copy.createdAt != null) {
            const createdTs =
              typeof copy.createdAt === 'number'
                ? copy.createdAt
                : Date.parse(copy.createdAt as string);
            copy.createdAt = Number.isFinite(createdTs) ? createdTs : undefined;
          }
          return ensureLocalStructures(copy as Lot);
        })
        .filter(Boolean) as Lot[];
    } else {
      state.lots = [];
    }
  } catch {
    state.lots = [];
  }
}
